import React, { useEffect, useRef, useState } from 'react';
import { useCallbackRef } from 'use-callback-ref';

import styles from './flyout.css';

import PopoutContainer from '../popout/popout-container';
import { PopoutProps, getGeometry, baseStyles, GeometryCss } from '../popout/popout-helpers';
import classNames from 'classnames';

const ESC_KEY_CODE = 27;

interface FlyoutProps extends PopoutProps {
  /** Force flyout to stay open after menu item is clicked */
  lockOpenOnClick?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
}

/**
 * Flyout component, like a tooltip but triggers on some action
 *
 * @param {FlyoutProps} param
 *
 * @returns JSX.ELEMENT
 */
export const Flyout = ({
  trigger,
  children,
  xAlign,
  yAlign,
  containerProps,
  lockOpenOnClick,
  light,
  className,
  activeClass,
  disabled,
  width: containerWidth = 200,
  'data-testid': dataTestId = 'flyout'
}: FlyoutProps) => {
  const [visible, setVisible] = useState(false);
  // we don't need the state value here, we just need the setState function to trigger rerender for the component
  const [, forceUpdate] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useCallbackRef<HTMLSpanElement | null>(null, () => forceUpdate(Date.now()));
  const { current } = wrapperRef;
  const [geometry, setGeometry] = useState<GeometryCss>(getGeometry(current, { xAlign, yAlign }));

  useEffect(() => {
    setGeometry(getGeometry(current, { xAlign, yAlign }));
  }, [wrapperRef.current]);

  const handleKeydown = (evt: KeyboardEvent) =>
    evt.keyCode === ESC_KEY_CODE ? setVisible(false) : null;

  const removeListeners = () => {
    document.removeEventListener('keydown', handleKeydown);
    document.removeEventListener('mousedown', handleClick);
  };

  const addListeners = () => {
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('mousedown', handleClick);
  };

  const clickOnTarget = () => {
    if (!visible) {
      if (!disabled) {
        addListeners();
        setVisible(!visible);
      }
    } else {
      removeListeners();
      setVisible(!visible);
    }
  };

  const clickOutsideTarget = () => {
    removeListeners();
    setVisible(false);
  };

  const isWrapper = (target: EventTarget | null) =>
    wrapperRef.current && wrapperRef.current.contains(target as Node);

  const isMenu = (target: EventTarget | null) =>
    containerRef.current && containerRef.current.contains(target as Node);

  // This handler captures the click event from the children too
  const handleClick = ({ type, target }: { target: EventTarget | null; type: string }) => {
    if (isMenu(target) && lockOpenOnClick) {
      return;
    }
    if (isWrapper(target)) {
      removeListeners();
      if (type !== 'mousedown') {
        clickOnTarget();
      }
    } else {
      clickOutsideTarget();
    }
  };

  return (
    <span
      ref={wrapperRef}
      className={classNames([visible && activeClass, className, styles.wrapper])}
      onClick={handleClick}
      data-testid={dataTestId}
    >
      {trigger}
      {wrapperRef && (
        <PopoutContainer
          {...{
            ...containerProps,
            containerRef,
            containerWidth,
            geometry,
            style: { ...baseStyles(visible, containerWidth, geometry) },
            type: 'flyout',
            light
          }}
        >
          {children}
        </PopoutContainer>
      )}
    </span>
  );
};

export default Flyout;
